import { ofType } from 'redux-observable';
import { type Observable, from, of } from 'rxjs';
import { catchError, mergeMap, withLatestFrom } from 'rxjs/operators';
import { Weddings } from '@bridebook/models';
import { Gazetteer } from '@bridebook/toolbox/src/gazetteer';
import { appError } from '@bridebook/web/lib/app/actions';
import { setSearchLocation } from '@bridebook/web/lib/search/actions';
import {
  type ChangeWeddingCountryAction,
  SettingsActionTypes,
} from '@bridebook/web/lib/settings/action-types';
import {
  changeWeddingCountryCompleted,
  changeWeddingCountryFailed,
  showLocationChangeModal,
} from '@bridebook/web/lib/settings/actions';
import { WeddingCountryChangedStorage } from '@bridebook/web/lib/storage-manager/basic/wedding-country-changed';
import type { IEpicDeps } from '@bridebook/web/lib/types';
import { getCurrentUserId, selectUserLocale } from '@bridebook/web/lib/users/selectors';
import { getWeddingId } from '@bridebook/web/lib/weddings/selectors';

export const changeWeddingCountryEpic = (
  action$: Observable<ChangeWeddingCountryAction>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(SettingsActionTypes.CHANGE_WEDDING_COUNTRY),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { market },
        },
        state,
      ]) => {
        const weddingId = getWeddingId(state);
        const userId = getCurrentUserId(state);
        const userLocale = selectUserLocale(state);
        const wedding = Weddings._.getById(weddingId);
        const weddingCountryChangedStorage = WeddingCountryChangedStorage(userId as string);
        const hasSeenBudgetWarningModal = state.users.user?.actions?.hasSeenBudgetWarningModal;
        const doesStorageExist = weddingCountryChangedStorage.get()?.countryChanged;

        const modalAlreadyPresentedToUser = doesStorageExist || hasSeenBudgetWarningModal;

        if (!modalAlreadyPresentedToUser) {
          weddingCountryChangedStorage.set({ countryChanged: true });
        }

        return from(wedding.updateCountry(market)).pipe(
          mergeMap(() => [
            setSearchLocation({
              type: 'autocomplete',
              text: Gazetteer.getCountryName(market.country, userLocale),
            }),
            showLocationChangeModal(),
            changeWeddingCountryCompleted(),
          ]),
          catchError((error) =>
            of(
              changeWeddingCountryFailed(),
              appError({ error, feature: 'Settings => Change wedding country' }),
            ),
          ),
        );
      },
    ),
  );
